import { motion, AnimatePresence } from 'framer-motion'
import { GitBranch, MoreHorizontal } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/utils/cn'
import { Badge } from '@/components/common'
import { useConversationStore } from '@/stores/conversationStore'
import type { BranchStatus } from '@/types/conversation'
import type { RoundReadiness } from '@/stores/conversationStore'

/**
 * Tabular branch picker shown above the answer panel.
 *
 * Round model v2:
 *   - Each tab shows a per-branch readiness pill (`3/5`, `Ready`,
 *     `Terminated`, `Pruned`) driven by the conversation store.
 *   - Local depth is shown next to the name as `L<n>`.
 *   - A kebab menu exposes per-branch actions (rename, terminate, prune).
 *     Prune triggers a modal owned by the workspace via `onRequestPrune`.
 */

export interface BranchTabsBranch {
  _id: string
  name?: string
  thematicName?: string
  userRenamedName?: string
  status: string
  depth: number
  localDepth?: number
}

export interface BranchTabsProps {
  branches: BranchTabsBranch[]
  activeBranchId: string | null
  onBranchSelect: (branchId: string) => void
  onRequestRename?: (branchId: string) => void
  onRequestTerminate?: (branchId: string) => void
  onRequestPrune?: (branchId: string) => void
  className?: string
}

const statusDot: Record<BranchStatus, string> = {
  active: 'bg-light-icy-blue dark:bg-dark-muted-teal',
  terminated_natural: 'bg-emerald-500',
  terminated_manual: 'bg-amber-500',
  pruned: 'bg-red-400',
}

const readinessVariant: Record<
  RoundReadiness,
  { label: (n: number, t: number) => string; className: string }
> = {
  ready: {
    label: () => 'Ready',
    className:
      'border-emerald-300 text-emerald-700 dark:border-emerald-600 dark:text-emerald-300',
  },
  incomplete: {
    label: (n, t) => `${n}/${t}`,
    className:
      'border-amber-300 text-amber-700 dark:border-amber-600 dark:text-amber-300',
  },
  no_questions: {
    label: () => '—',
    className:
      'border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-400',
  },
  terminated: {
    label: () => 'Terminated',
    className:
      'border-slate-300 text-slate-500 dark:border-slate-600 dark:text-slate-400 line-through opacity-80',
  },
  pruned: {
    label: () => 'Pruned',
    className:
      'border-red-300 text-red-600 dark:border-red-600 dark:text-red-300 line-through opacity-80',
  },
}

export const BranchTabs = ({
  branches,
  activeBranchId,
  onBranchSelect,
  onRequestRename,
  onRequestTerminate,
  onRequestPrune,
  className,
}: BranchTabsProps) => {
  const getRoundReadiness = useConversationStore((s) => s.getRoundReadiness)
  const getBranchProgress = useConversationStore((s) => s.getBranchProgress)
  const [openMenuFor, setOpenMenuFor] = useState<string | null>(null)

  const activeBranches = branches.filter((b) => b.status === 'active')
  const completedCount = branches.filter(
    (b) => b.status === 'terminated_natural' || b.status === 'terminated_manual',
  ).length
  const prunedCount = branches.filter((b) => b.status === 'pruned').length
  const readyCount = activeBranches.reduce(
    (acc, b) => (getRoundReadiness(b._id) === 'ready' ? acc + 1 : acc),
    0,
  )

  // Only active branches appear in the tab strip. Terminated/pruned
  // branches are still reachable from the sidebar (read-only chat view)
  // and from the tree, but they don't clutter the tabs.
  const ordered = activeBranches

  if (ordered.length === 0) return null

  const hasMenuActions = !!(onRequestRename || onRequestTerminate || onRequestPrune)

  return (
    <div
      className={cn(
        // Sticky inside the conversation scroll container so the strip
        // pins to the top once the user scrolls past it. z-30 keeps it
        // above DepthDivider (z-10) and the new-messages pill (z-20).
        'sticky top-0 z-30 rounded-md',
        'border border-slate-200 dark:border-slate-700',
        'bg-white/90 dark:bg-slate-900/90 backdrop-blur shadow-sm',
        className,
      )}
    >
      <div className="flex items-center gap-1 px-2 overflow-x-auto">
        <AnimatePresence mode="popLayout">
          {ordered.map((branch) => {
            const isActive = branch._id === activeBranchId
            const displayName =
              branch.userRenamedName ||
              branch.thematicName ||
              branch.name ||
              `Branch ${branch._id.slice(-6)}`
            const status = (branch.status as BranchStatus) ?? 'active'
            const isInactive = status !== 'active'
            const readiness = getRoundReadiness(branch._id)
            const progress = getBranchProgress(branch._id)
            const variant = readinessVariant[readiness]
            const localDepth = branch.localDepth ?? branch.depth

            return (
              <div key={branch._id} className="relative flex-shrink-0">
                <motion.button
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  onClick={() => onBranchSelect(branch._id)}
                  className={cn(
                    'group flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors',
                    'border-b-2 -mb-px',
                    isActive
                      ? 'text-light-icy-blue dark:text-dark-muted-teal border-light-icy-blue dark:border-dark-muted-teal'
                      : 'text-slate-600 dark:text-slate-400 border-transparent hover:text-slate-900 dark:hover:text-white hover:border-slate-300 dark:hover:border-slate-600',
                    isInactive && 'opacity-70',
                  )}
                >
                  <span className={cn('w-2 h-2 rounded-full', statusDot[status])} />
                  <GitBranch className="w-4 h-4" />
                  <span className="max-w-[140px] truncate">{displayName}</span>
                  {localDepth > 0 && (
                    <Badge size="sm" variant="outline" className="text-xs">
                      L{localDepth}
                    </Badge>
                  )}
                  <Badge
                    size="sm"
                    variant="outline"
                    className={cn('text-xs', variant.className)}
                  >
                    {variant.label(progress.answered, progress.total)}
                  </Badge>
                </motion.button>
                {hasMenuActions && (
                  <button
                    type="button"
                    aria-label={`Branch options for ${displayName}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      setOpenMenuFor(
                        openMenuFor === branch._id ? null : branch._id,
                      )
                    }}
                    className={cn(
                      'absolute top-1 right-0 p-1 rounded',
                      'text-slate-400 hover:text-slate-700 dark:hover:text-slate-200',
                      'opacity-0 group-hover:opacity-100 focus:opacity-100',
                    )}
                  >
                    <MoreHorizontal className="w-3 h-3" />
                  </button>
                )}
                {openMenuFor === branch._id && hasMenuActions && (
                  <div
                    className={cn(
                      'absolute right-0 top-full z-20 mt-1 w-44 py-1',
                      'rounded-md border border-slate-200 dark:border-slate-700',
                      'bg-white dark:bg-slate-900 shadow-lg text-sm',
                    )}
                    onMouseLeave={() => setOpenMenuFor(null)}
                  >
                    {onRequestRename && (
                      <button
                        type="button"
                        onClick={() => {
                          setOpenMenuFor(null)
                          onRequestRename(branch._id)
                        }}
                        className="block w-full text-left px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-800"
                      >
                        Rename branch
                      </button>
                    )}
                    {onRequestTerminate && status === 'active' && (
                      <button
                        type="button"
                        onClick={() => {
                          setOpenMenuFor(null)
                          onRequestTerminate(branch._id)
                        }}
                        className="block w-full text-left px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-800"
                      >
                        Terminate branch
                      </button>
                    )}
                    {onRequestPrune && status !== 'pruned' && (
                      <button
                        type="button"
                        onClick={() => {
                          setOpenMenuFor(null)
                          onRequestPrune(branch._id)
                        }}
                        className="block w-full text-left px-3 py-1.5 text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40"
                      >
                        Prune branch…
                      </button>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </AnimatePresence>
      </div>

      <div className="px-4 py-1 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 flex items-center gap-3">
        <span>
          {activeBranches.length} active · {readyCount} ready
        </span>
        <span>· {completedCount} completed</span>
        {prunedCount > 0 && <span>· {prunedCount} pruned</span>}
      </div>
    </div>
  )
}

export default BranchTabs
